"use client";

import {useState} from "react";
import {Bell, LucideX, TrendingDown} from "lucide-react";
import Link from "next/link";
import dynamic from "next/dynamic";
import {Button} from "@/shadcn/components/ui/button";
import {Input} from "@/shadcn/components/ui/input";
import {createPriceAlert} from "@/app/actions/price-alert-actions";

// Lazy-load modal components only when needed (avoids hydration issues from useMediaQuery)
const Dialog = dynamic(() => import("@/shadcn/components/ui/dialog").then(m => m.Dialog), {ssr: false});
const DialogContent = dynamic(() => import("@/shadcn/components/ui/dialog").then(m => m.DialogContent), {ssr: false});
const DialogHeader = dynamic(() => import("@/shadcn/components/ui/dialog").then(m => m.DialogHeader), {ssr: false});
const DialogTitle = dynamic(() => import("@/shadcn/components/ui/dialog").then(m => m.DialogTitle), {ssr: false});
const DialogDescription = dynamic(() => import("@/shadcn/components/ui/dialog").then(m => m.DialogDescription), {ssr: false});

const Drawer = dynamic(() => import("@/shadcn/components/ui/drawer").then(m => m.Drawer), {ssr: false});
const DrawerClose = dynamic(() => import("@/shadcn/components/ui/drawer").then(m => m.DrawerClose), {ssr: false});
const DrawerContent = dynamic(() => import("@/shadcn/components/ui/drawer").then(m => m.DrawerContent), {ssr: false});
const DrawerHeader = dynamic(() => import("@/shadcn/components/ui/drawer").then(m => m.DrawerHeader), {ssr: false});
const DrawerTitle = dynamic(() => import("@/shadcn/components/ui/drawer").then(m => m.DrawerTitle), {ssr: false});
const DrawerDescription = dynamic(() => import("@/shadcn/components/ui/drawer").then(m => m.DrawerDescription), {ssr: false});

interface PriceAlertModalProps {
    productTitle: string;
    productImage?: string;
    currentPrice: number;
    productEan: string;
    variantId?: string;
}

function formatPrice(price: number): string {
    return price.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €';
}

export function PriceAlertModal({productTitle, productImage, currentPrice, productEan, variantId}: PriceAlertModalProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [hasOpened, setHasOpened] = useState(false); // Track if modal was ever opened
    const [email, setEmail] = useState("");
    const [targetPrice, setTargetPrice] = useState(Math.round(currentPrice * 0.9));
    const [acceptPrivacy, setAcceptPrivacy] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState("");

    // Detect mobile on client only - safe because we only check after hasOpened
    const [isMobile, setIsMobile] = useState(false);

    // Min und Max für den Slider
    const minPrice = Math.round(currentPrice * 0.5);
    const maxPrice = Math.round(currentPrice * 0.99);

    // Prozentuale Ersparnis berechnen
    const savingsPercent = Math.round(((currentPrice - targetPrice) / currentPrice) * 100);

    const handleOpen = () => {
        // Detect mobile only when opening (client-side only)
        setIsMobile(window.innerWidth < 640);
        setHasOpened(true);
        setIsOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!email.trim()) {
            setError("Bitte gib deine E-Mail-Adresse ein.");
            return;
        }

        if (!acceptPrivacy) {
            setError("Bitte stimme der Datenschutzerklärung zu.");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError("Bitte gib eine gültige E-Mail-Adresse ein.");
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await createPriceAlert({
                email,
                targetPrice,
                currentPrice,
                productTitle,
                productImage,
                productEan,
                variantId,
                privacyAccepted: acceptPrivacy,
            });

            if (result.success) {
                setIsSuccess(true);
            } else {
                setError(result.message || "Ein Fehler ist aufgetreten.");
            }
        } catch {
            setError("Ein Fehler ist aufgetreten. Bitte versuche es später erneut.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeModal = () => {
        setIsOpen(false);
        setTimeout(() => {
            setIsSuccess(false);
            setError("");
        }, 300);
    };

    // Form Content - as JSX variable (not a function) to prevent re-mounting on every render
    const formContent = (
        <div className="p-5">
            {isSuccess ? (
                <div className="text-center py-8">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <TrendingDown className="w-8 h-8 text-emerald-600"/>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                        Preisalarm aktiviert!
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                        Wir benachrichtigen dich unter <strong>{email}</strong>, sobald der Preis auf {formatPrice(targetPrice)} oder darunter fällt.
                    </p>
                    <Button
                        onClick={closeModal}
                        variant="primary"
                        className="py-3 px-6 rounded-xl"
                    >
                        Verstanden
                    </Button>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* E-Mail Input */}
                    <div>
                        <label htmlFor="price-alert-email" className="block text-sm font-medium text-slate-700 mb-2">
                            E-Mail-Adresse
                        </label>
                        <Input
                            type="email"
                            id="price-alert-email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Deine E-Mail"
                            className="placeholder:text-sm placeholder:text-slate-300 placeholder:font-normal text-[16px] focus:ring-1 focus:ring-amber-500 w-full"
                            autoCapitalize="none"
                            autoComplete="email"
                            autoCorrect="off"
                        />
                    </div>

                    {/* Price Slider */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Benachrichtigen, wenn der Preis fällt auf:
                        </label>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-3">
                            <div className="flex items-baseline justify-center gap-2">
                                <span className="text-3xl font-bold text-emerald-700">{formatPrice(targetPrice)}</span>
                                <span className="text-sm font-semibold text-emerald-600">(-{savingsPercent}%)</span>
                            </div>
                        </div>
                        <input
                            type="range"
                            min={minPrice}
                            max={maxPrice}
                            step={1}
                            value={targetPrice}
                            onChange={(e) => setTargetPrice(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                        />
                        <div className="flex justify-between text-xs text-slate-500 mt-2">
                            <span>{formatPrice(minPrice)} (-50%)</span>
                            <span>{formatPrice(maxPrice)}</span>
                        </div>
                    </div>

                    {/* Privacy Checkbox */}
                    <div className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            id="price-alert-privacy"
                            checked={acceptPrivacy}
                            onChange={(e) => setAcceptPrivacy(e.target.checked)}
                            className="w-5 h-5 mt-0.5 rounded border-gray-300 text-amber-500 focus:ring-amber-500 cursor-pointer"
                        />
                        <label htmlFor="price-alert-privacy" className="text-sm text-slate-600 cursor-pointer">
                            Ich stimme der{' '}
                            <Link href="/datenschutz" target="_blank" className="text-amber-600 hover:underline">
                                Datenschutzerklärung
                            </Link>{' '}
                            zu und möchte Preisalarm-Benachrichtigungen per E-Mail erhalten.
                        </label>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700">
                            {error}
                        </div>
                    )}

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        variant="primary"
                        className="w-full py-3.5 rounded-xl"
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Wird gespeichert...
                            </>
                        ) : (
                            <>
                                <Bell className="w-5 h-5"/>
                                Preisalarm aktivieren
                            </>
                        )}
                    </Button>

                    {/* Info Text */}
                    <p className="text-xs text-slate-400 text-center">
                        Du erhältst maximal eine E-Mail pro Tag für dieses Produkt.
                    </p>
                </form>
            )}
        </div>
    );

    return (
        <>
            {/* Trigger Button - always rendered, no hydration issues */}
            <Button
                onClick={handleOpen}
                variant="outline"
                className="w-full mb-4"
            >
                <Bell className="w-4 h-4"/>
                Preisalarm setzen
            </Button>

            {/* Modal - only rendered after first open (client-side only) */}
            {hasOpened && !isMobile && (
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                    <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
                        <DialogHeader className="p-5 pb-4 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                                    <Bell className="w-5 h-5 text-slate-600"/>
                                </div>
                                <div>
                                    <DialogTitle className="font-bold text-slate-900">Preisalarm</DialogTitle>
                                    <DialogDescription className="text-xs text-slate-500">
                                        Nie mehr ein Schnäppchen verpassen
                                    </DialogDescription>
                                </div>
                            </div>
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={closeModal}
                                className="active:bg-slate-200 hover:bg-slate-200 absolute right-4 top-4 border-slate-200"
                            >
                                <LucideX className="h-4 w-4"/>
                            </Button>
                        </DialogHeader>
                        {formContent}
                    </DialogContent>
                </Dialog>
            )}

            {hasOpened && isMobile && (
                <Drawer open={isOpen} onOpenChange={setIsOpen}>
                    <DrawerContent className="max-h-[90vh]">
                        <DrawerHeader className="p-5 pb-4 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                                    <Bell className="w-5 h-5 text-slate-600"/>
                                </div>
                                <div className="text-left">
                                    <DrawerTitle className="font-bold text-slate-900">Preisalarm</DrawerTitle>
                                    <DrawerDescription className="text-xs text-slate-500">
                                        Nie mehr ein Schnäppchen verpassen
                                    </DrawerDescription>
                                </div>
                            </div>
                            <DrawerClose asChild>
                                <Button
                                    size="icon"
                                    variant="outline"
                                    className="active:bg-slate-200 hover:bg-slate-200 absolute right-4 top-4 border-slate-200"
                                >
                                    <LucideX className="h-4 w-4"/>
                                </Button>
                            </DrawerClose>
                        </DrawerHeader>
                        <div className="overflow-y-auto">
                            {formContent}
                        </div>
                        <div className="h-safe-area-inset-bottom pb-4"/>
                    </DrawerContent>
                </Drawer>
            )}
        </>
    );
}
